/* Copyright (c) 2020-present CloudIO Inc */

import {
  DataRecord,
  MULTIPLE_NUMBER_OPS,
  NUMBER_OPS_KEY_TYPE,
  OPS_ICONS,
} from '@cloudio-saas/datasource-types';
import { useEvent } from '@cloudio-saas/statex';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField as MUINumberField,
  TextFieldProps as MUINumberFieldProps,
  Slider,
  Typography,
} from '@mui/material';
import { BaseProps, Form, SearchNumberField } from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { useNumberFieldStyles } from 'components/NumberField/NumberField';
import {
  allNumberOps,
  hasNumberEditor,
} from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import { useFocus } from 'hooks/useFocus';
import Icon from 'icons/Icon';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useStoreAttributeFilter } from 'store/useAppStore';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import { useTranslate } from '../../utils/lang';
import { useStringExpression } from 'expression/useExpression';
import zIndex from 'constants/ZIndex';

interface NumberFieldProps
  extends Required<SearchNumberField.SearchNumberField>,
    BaseProps<'SearchNumberField'> {
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

interface Props extends NumberFieldProps, Form.HorizontalFormProps {}

function validate(props: Props, _value: number): string | null {
  const { allowNegative, allowDecimal } = props;
  if (!allowNegative && _value < 0) {
    return 'You must enter only positive value';
  }
  if (!allowDecimal && Math.round(_value) !== _value) {
    return 'Decimal values are not allowed';
  }
  return null;
}

function IONumberField(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    children,
    className,
    defaultOperator,
    domProps,
    flex,
    formHorizontal,
    hideOperatorChooser,
    itemId,
    labelAlignment,
    labelWidth,
    platform,
    required,
    viewAttribute,
    headerFilter,
    pageId,
    useSlider,
    step,
    minValue,
    maxValue,
    onHeaderFilterSearch,
  } = props;
  const classes = useNumberFieldStyles();

  let { helperText, placeholder } = props;
  const { label } = props;
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;

  const { t } = useTranslate();
  helperText = t(helperText);
  placeholder = t(placeholder);
  labelResult = t(labelResult);

  const variant = useFieldVariant(appUid);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording()) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<NUMBER_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState('');
  const [sliderValue, setSliderValue] = useState<number[]>(
    defaultOperator === 'bn' ? [minValue, minValue + step] : [minValue],
  );
  const options = getOpOptions('Number', t);
  const showSlider =
    useSlider && !['null', 'notNull', 'in', 'nin'].includes(ops);

  const inputRef = useFocus(itemId);
  const focusedValue = useRef(value);
  const currentValue = useRef(value);
  useEffect(() => {
    currentValue.current = value;
  }, [value]);
  const isFocused = useRef(false);

  const store = useCurrentDataStore<DataRecord>();
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);

  useEffect(() => {
    if (filter) {
      const op = getOperator(filter) as NUMBER_OPS_KEY_TYPE;
      setOps(op);
      const _hasEditor = hasNumberEditor(op);
      if (!_hasEditor) {
        setValue('');
      } else {
        const val = getValue(filter) as number | number[];
        let v = '';
        if (Array.isArray(val)) {
          v = val.join(', ');
        } else {
          v = val.toString();
        }
        setValue(v);
        if (headerFilter && focusedValue.current === '') {
          // on header filter, the focus is set before useEffect is called
          // so we need to set the focusedValue here
          focusedValue.current = v;
        }
      }
    } else {
      // #974 Fix. Empty/Not Empty filter has to be set to default operator
      setOps(defaultOperator);
      setValue('');
    }
  }, [defaultOperator, filter, headerFilter]);
  const hasEditor = hasNumberEditor(ops);

  function updateFilter(attr: string, op: NUMBER_OPS_KEY_TYPE, val: string) {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== attr);
    const _hasEditor = hasNumberEditor(op);
    if (!_hasEditor || isNotEmpty(val)) {
      let v: string | number | number[] = _hasEditor ? val : 0;
      if (op in MULTIPLE_NUMBER_OPS) {
        v = val
          .split(',')
          .map((_v) => _v.trim())
          .filter(isNotEmpty)
          .map(Number);
      } else {
        v = Number(val);
      }
      const _filter = {
        [attr]: { [op]: v },
      };
      filters.push(_filter);
    } else {
      setValue('');
    }

    store?.setSmartSeachFilter(filters);
  }

  const onBlur = useEvent(async () => {
    isFocused.current = false;
    if (focusedValue.current !== currentValue.current) {
      focusedValue.current = currentValue.current;
      updateFilter(viewAttribute, ops, currentValue.current);
    }
  });

  if (ops in MULTIPLE_NUMBER_OPS) {
    placeholder = `e.g. 1, 2, 3${placeholder ? ` (${placeholder})` : ''}`;
  }
  const id = useId();

  const numberFieldProps: MUINumberFieldProps = {
    id,
    fullWidth: true,
    autoFocus,
    variant,
    helperText,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = isEmpty(e.target.value)
        ? undefined
        : Number(e.target.value);
      if (isEmpty(newValue) || !Number.isNaN(newValue)) {
        const errMsg =
          newValue === undefined ? undefined : validate(props, newValue);
        if (errMsg) {
          platform.showError(errMsg);
          return;
        }
        currentValue.current =
          newValue === undefined ? '' : newValue.toString();
        setValue(newValue === undefined ? '' : newValue.toString());
        if (platform.isRecording() && !headerFilter) {
          platform.recordLine(
            itemId,
            `await ui.type('${itemId}', ${
              newValue === undefined ? "''" : newValue
            });`,
          );
        }
      }
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onBlur();
        onHeaderFilterSearch?.();
      }
    },
    InputProps: {
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        isFocused.current = true;
      },
      onBlur,
      inputRef,
      startAdornment: hideOperatorChooser ? undefined : (
        <InputAdornment position="start">
          <IconButton
            data-testid={`${itemId}-op`}
            onClick={handleClick}
            data-tip={allNumberOps[ops] || ops}
          >
            <Icon iconName={OPS_ICONS[ops] || 'equals'} />
          </IconButton>
        </InputAdornment>
      ),
    },
    inputProps: {
      className: classes.numberInput,
    },
    type: 'number',
    required,
    placeholder: hasEditor ? placeholder : allNumberOps[ops],
    disabled: !hasEditor,
  };

  const sliderProps = {
    value: sliderValue,
    min: minValue,
    max: maxValue,
    step,
    onChange: (e: any, newValue: number | number[]) => {
      const _newValue = Array.isArray(newValue) ? newValue : [newValue];
      setSliderValue(_newValue);
      updateFilter(viewAttribute, ops, _newValue.join(','));
      if (platform.isRecording() && !headerFilter) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', ${
            newValue === undefined ? "''" : newValue
          });`,
        );
      }
    },
  };

  useEffect(() => {
    return () => {
      if (isFocused.current) onBlur();
    };
  }, [onBlur]);
  labelResult =
    ops === 'eq' ? labelResult : `${labelResult} (${allNumberOps[ops]})`;

  return (
    <>
      <FieldWrapper
        className={className}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal ? (
          <HorizontalFormField
            control={
              showSlider ? (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!hideOperatorChooser ? (
                    <IconButton
                      data-testid={`${itemId}-op`}
                      onClick={handleClick}
                      data-tip={allNumberOps[ops] || ops}
                    >
                      <Icon iconName={OPS_ICONS[ops] || 'equals'} />
                    </IconButton>
                  ) : null}
                  <Slider
                    valueLabelDisplay="auto"
                    disableSwap
                    size="small"
                    {...sliderProps}
                  />
                </Box>
              ) : (
                <MUINumberField
                  {...numberFieldProps}
                  hiddenLabel
                  size="small"
                />
              )
            }
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : showSlider ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {labelResult}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {!hideOperatorChooser ? (
                <IconButton
                  data-testid={`${itemId}-op`}
                  onClick={handleClick}
                  data-tip={allNumberOps[ops] || ops}
                >
                  <Icon iconName={OPS_ICONS[ops] || 'equals'} />
                </IconButton>
              ) : null}
              <Slider
                valueLabelDisplay="auto"
                disableSwap
                size="small"
                {...sliderProps}
              />
            </Box>
          </Box>
        ) : (
          <MUINumberField
            {...numberFieldProps}
            label={headerFilter ? undefined : labelResult}
            hiddenLabel={headerFilter}
          />
        )}
        {children}
      </FieldWrapper>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{ zIndex: zIndex.Tooltip + 1 }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as NUMBER_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(
                  viewAttribute,
                  op,
                  showSlider
                    ? op === 'bn'
                      ? sliderValue.length === 1
                        ? [
                            Number(sliderValue[0]),
                            Number(sliderValue[0]) + step,
                          ].join(',')
                        : sliderValue.join(',')
                      : `${sliderValue[0]}`
                    : value,
                );
                if (showSlider) {
                  setSliderValue(
                    op === 'bn'
                      ? sliderValue.length === 1
                        ? [
                            Number(sliderValue[0]),
                            Number(sliderValue[0]) + step,
                          ]
                        : sliderValue
                      : [sliderValue[0]],
                  );
                }
                if (hasNumberEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default React.forwardRef(IONumberField);
